import {useEffect, useState} from 'react'

/**
 * The goal of this hook is to prevent spam of API calls when filters/arguments frequently changes.
 * @param value Changing value to debounce. This hook compares old&new value by reference!
 * @param delayMs Delay of timeout, that waits to save&return new value.
 * @returns debounced value through setTimeout + useState
 *
 * @example
 * const [value, setValue] = useState('')
 * const debouncedValue = useDebouncedValue(value)
 * const apiData = useQuery({variables: debouncedValue})
 *
 * <input value={value} onChange={(e) => setValue(e.target.value)} />
 */
export default function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedValue(value), delayMs)
    return () => clearTimeout(timeoutId)
  }, [delayMs, value])

  return debouncedValue
}
